import { Box, Grid } from '@mui/material';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  LegacyAutocomplete,
  LegacyMuiTextField,
} from '~/legacy-components/LegacyForms';

import { EmpOffContactType, Office } from '../../data';
import { IFormInput } from '../../useWorkContact';

import { useSxStyles } from './useStyles';

type Props = {
  data: EmpOffContactType;
  register: UseFormRegister<IFormInput>;
  errors: FieldErrors<IFormInput>;
  companyOffice: Office[];
  control: Control<IFormInput>;
};

export const WorkContactEditMode = ({
  register,
  errors,
  companyOffice,
  control,
  data: emp,
}: Props) => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();
  return (
    <div>
      <Box component="table" sx={sxStyles.tableEdit}>
        <tbody>
          <tr>
            <td>
              <Box
                component="label"
                htmlFor="fEmpPhoneWork"
                sx={sxStyles.label}
              >
                {t('fEmpPhoneWork')}:
              </Box>
            </td>
          </tr>
          <tr>
            <Box component="td" sx={sxStyles.inputTableItem}>
              <Box
                component="input"
                {...register('fEmpPhoneWork')}
                type="text"
                sx={errors.fEmpPhoneWork ? sxStyles.errorMsg : sxStyles.input}
                defaultValue={emp?.fEmpPhoneWork}
              />
            </Box>
          </tr>
          <tr>
            <td>
              {errors.fEmpPhoneWork && (
                <Grid item>
                  <Box component="span" sx={sxStyles.errorMsgText}>
                    {errors.fEmpPhoneWork.message}
                  </Box>
                </Grid>
              )}
            </td>
          </tr>
          <tr>
            <td>
              <Box component="label" htmlFor="fEmpPhoneMob" sx={sxStyles.label}>
                {t('fEmpPhoneMob')}:
              </Box>
            </td>
          </tr>
          <tr>
            <Box component="td" sx={sxStyles.inputTableItem}>
              <Box
                component="input"
                {...register('fEmpPhoneMob')}
                type="text"
                sx={errors.fEmpPhoneMob ? sxStyles.errorMsg : sxStyles.input}
                defaultValue={emp?.fEmpPhoneMob}
              />
            </Box>
          </tr>
          <tr>
            <td>
              {errors.fEmpPhoneMob && (
                <Grid item>
                  <Box component="span" sx={sxStyles.errorMsgText}>
                    {errors.fEmpPhoneMob.message}
                  </Box>
                </Grid>
              )}
            </td>
          </tr>
          <tr>
            <td>
              <Box
                component="label"
                htmlFor="fEmpEmailWork"
                sx={sxStyles.label}
              >
                {t('fEmpEmailWork')}:{' '}
                <Box component="i" sx={sxStyles.required}></Box>
              </Box>
            </td>
          </tr>
          <tr>
            <Box component="td" sx={sxStyles.inputTableItem}>
              <Box
                component="input"
                {...register('fEmpEmailWork')}
                type="text"
                sx={errors.fEmpEmailWork ? sxStyles.errorMsg : sxStyles.input}
                defaultValue={emp?.fEmpEmailWork}
              />
            </Box>
          </tr>
          <tr>
            <td>
              {errors.fEmpEmailWork && (
                <Grid item>
                  <Box component="span" sx={sxStyles.errorMsgText}>
                    {errors.fEmpEmailWork.message}
                  </Box>
                </Grid>
              )}
            </td>
          </tr>
          <tr>
            <td>
              <Box
                component="label"
                htmlFor="fEmpOfficeLoc"
                sx={sxStyles.label}
              >
                {t('fEmpOfficeLoc')}:{' '}
                <Box component="i" sx={sxStyles.required}></Box>
              </Box>
            </td>
          </tr>
          <tr>
            <Box component="td" sx={sxStyles.inputTableItem}>
              <Controller
                control={control}
                name="fEmpOfficeLoc"
                defaultValue={emp?.fEmpOfficeLoc}
                render={({ field: { onChange, value } }) => (
                  <LegacyAutocomplete
                    fullWidth
                    sx={errors.fEmpOfficeLoc && sxStyles.errorMsgSelect}
                    options={companyOffice}
                    getOptionLabel={(c) => c.fOfficeCity}
                    onChange={(e, value) => {
                      onChange(value?.fOfficeId || '');
                    }}
                    value={
                      companyOffice.find((op) => op.fOfficeId === value) || null
                    }
                    renderInput={(params) => (
                      <LegacyMuiTextField {...params} placeholder={' '} />
                    )}
                  />
                )}
              />
            </Box>
          </tr>
          <tr>
            <td>
              {errors.fEmpOfficeLoc && (
                <Grid item>
                  <Box component="span" sx={sxStyles.errorMsgText}>
                    {errors.fEmpOfficeLoc.message}
                  </Box>
                </Grid>
              )}
            </td>
          </tr>
          <tr>
            <td>
              <Box
                component="label"
                htmlFor="fEmpOfficeFloor"
                sx={sxStyles.label}
              >
                {t('fEmpOfficeFloor')}:
              </Box>
            </td>
          </tr>
          <tr>
            <Box component="td" sx={sxStyles.inputTableItem}>
              <Box
                component="input"
                {...register('fEmpOfficeFloor')}
                type="text"
                sx={errors.fEmpOfficeFloor ? sxStyles.errorMsg : sxStyles.input}
                defaultValue={emp?.fEmpOfficeFloor}
              />
            </Box>
          </tr>
          <tr>
            <td>
              {errors.fEmpOfficeFloor && (
                <Grid item>
                  <Box component="span" sx={sxStyles.errorMsgText}>
                    {errors.fEmpOfficeFloor.message}
                  </Box>
                </Grid>
              )}
            </td>
          </tr>
          <tr>
            <td>
              <Box
                component="label"
                htmlFor="fEmpOfficeRoom"
                sx={sxStyles.label}
              >
                {t('fEmpOfficeRoom')}:
              </Box>
            </td>
          </tr>
          <tr>
            <Box component="td" sx={sxStyles.inputTableItem}>
              <Box
                component="input"
                {...register('fEmpOfficeRoom')}
                type="text"
                sx={errors.fEmpOfficeRoom ? sxStyles.errorMsg : sxStyles.input}
                defaultValue={emp?.fEmpOfficeRoom}
              />
            </Box>
          </tr>
          <tr>
            <td>
              {errors.fEmpOfficeRoom && (
                <Grid item>
                  <Box component="span" sx={sxStyles.errorMsgText}>
                    {errors.fEmpOfficeRoom.message}
                  </Box>
                </Grid>
              )}
            </td>
          </tr>
        </tbody>
      </Box>
    </div>
  );
};
